import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  ShieldCheck,
  Sparkles,
  Users,
  CalendarCheck,
  MessageCircle,
  ArrowRight,
  Star,
  ChevronDown,
  Bot,
} from "lucide-react";
import Footer from "@/components/Footer";

const APP_STORE_URL = "https://apps.apple.com/app/id6757411835";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.recoverymatch.dating";

const serif = { fontFamily: "'Playfair Display', serif" };
const sans = { fontFamily: "'Manrope', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

const NAVY = "#0A2A5A";
const GOLD = "#F9AB0D";
const CREAM = "#F9F6F2";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const StoreBadges = ({ height = "h-11" }) => (
  <div className="flex items-center gap-3 flex-wrap">
    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform" data-testid="app-store-badge">
      <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on App Store" className={height} />
    </a>
    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-transform" data-testid="google-play-badge">
      <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className={height} />
    </a>
  </div>
);

const successStories = [
  {
    names: "Sarah & Michael",
    image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400&h=500&fit=crop",
    quote: "We were matched based on our values and recovery journeys. Three years sober together and counting.",
    duration: "Together 2 years",
  },
  {
    names: "James & Emily",
    image: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400&h=500&fit=crop",
    quote: "Finding someone who understands recovery made all the difference. We got engaged last month!",
    duration: "Together 18 months",
  },
  {
    names: "David & Chris",
    image: "https://images.unsplash.com/photo-1516914943479-89db7d9ae7f2?w=400&h=500&fit=crop",
    quote: "RecoveryMatch gave us the safe space to be honest about our journeys from day one.",
    duration: "Together 14 months",
  },
];

const faqs = [
  { q: "Who is RecoveryMatch for?", a: "RecoveryMatch is for adults (18+) in recovery from alcohol and substance use who want meaningful romantic connections with people who understand their journey." },
  { q: "Is my recovery information private?", a: "Yes! You control what you share. Your sobriety date and program details are only visible to other members, and you can adjust privacy settings anytime." },
  { q: "How does matching work?", a: "We use a detailed compatibility algorithm based on your recovery journey, values, and relationship goals. You receive curated daily matches (3-5 per day) with compatibility scores. Express interest, and if it's mutual, you can start chatting!" },
  { q: "Is RecoveryMatch free?", a: "Yes! Basic features are free with 3 matches per day. Recovery+ ($9.99/mo) lets you see who liked you and like them back instantly. Recovery Elite ($19.99/mo) adds Super Likes, video calls, and advanced filters." },
];

const appScreens = [
  { title: "Daily Matches", image: "/images/app-v2-daily-matches.jpeg" },
  { title: "My Journey", image: "/images/app-v2-journey.jpeg" },
  { title: "It's a Match", image: "/images/app-v2-match.jpeg" },
  { title: "Messages", image: "/images/app-v2-messages.jpeg" },
  { title: "Guided Chat", image: "/images/app-v2-chat.jpeg" },
  { title: "Community", image: "/images/app-v2-community.jpeg" },
  { title: "Find a Meeting", image: "/images/app-v2-meetings.jpeg" },
  { title: "Spin & Win", image: "/images/app-v2-spin-wheel.jpeg" },
  { title: "Your Profile", image: "/images/app-v2-profile.jpeg" },
];

export default function LandingPageV2() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={{ ...sans, backgroundColor: CREAM }} className="min-h-screen text-slate-700 overflow-x-hidden" data-testid="landing-page">
      {/* Sticky glass nav */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ backgroundColor: "rgba(249,246,242,0.85)", borderColor: "rgba(10,42,90,0.1)" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo-icon.png" alt="RecoveryMatch" className="w-10 h-10 rounded-xl" data-testid="nav-logo" />
            <div className="text-xl sm:text-2xl font-bold" style={serif}>
              <span style={{ color: NAVY }}>Recovery</span>
              <span style={{ color: GOLD }}>Match</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-10 text-sm font-semibold" style={{ color: NAVY }}>
            <a href="#features" className="hover:opacity-70 transition-opacity">Why Us</a>
            <a href="#app" className="hover:opacity-70 transition-opacity">The App</a>
            <a href="#stories" className="hover:opacity-70 transition-opacity">Stories</a>
            <a href="#faq" className="hover:opacity-70 transition-opacity">FAQ</a>
          </nav>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              className="text-sm font-bold hover:opacity-70 transition-opacity"
              style={{ color: NAVY }}
              onClick={() => navigate("/login")}
              data-testid="nav-login-btn"
            >
              Log In
            </button>
            <button
              className="rounded-full px-5 sm:px-6 py-2.5 text-sm font-bold transition-transform hover:scale-[1.03] active:scale-[0.98] shadow-sm"
              style={{ backgroundColor: GOLD, color: NAVY }}
              onClick={() => navigate("/register")}
              data-testid="nav-join-btn"
            >
              Join Free
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl" style={{ backgroundColor: GOLD }} />
        <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl" style={{ backgroundColor: "#4A7BD0" }} />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-0 grid md:grid-cols-12 gap-12 items-end relative">
          <div className="md:col-span-6 pb-16 sm:pb-24">
            <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold mb-6" style={{ ...mono, color: GOLD }}>
              The dating app for people in recovery
            </motion.p>
            <motion.h1
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-8"
              style={serif}
              data-testid="hero-title"
            >
              Love, on the <span className="italic" style={{ color: GOLD }}>clearest</span> day of your life.
            </motion.h1>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="text-base sm:text-lg leading-relaxed mb-10 max-w-md"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              RecoveryMatch connects clean and sober singles who understand the journey. Real people. Real clean
              time. Real connection — without the pressure of explaining yourself.
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="flex flex-wrap items-center gap-4 mb-8">
              <button
                className="rounded-full px-8 py-4 font-bold text-base transition-transform hover:scale-[1.03] active:scale-[0.98] shadow-lg flex items-center gap-2"
                style={{ backgroundColor: GOLD, color: NAVY }}
                onClick={() => navigate("/register")}
                data-testid="hero-cta"
              >
                Start Your Story <ArrowRight size={18} />
              </button>
              <a
                href="#app"
                className="rounded-full px-8 py-4 font-semibold text-base border-2 text-white transition-colors hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.3)" }}
                data-testid="hero-app-cta"
              >
                Get the App
              </a>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.35 }}>
              <StoreBadges height="h-10" />
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }} className="mt-10 flex items-center gap-3">
              <div className="flex -space-x-3">
                {[11, 32, 45, 59].map((n) => (
                  <img key={n} src={`https://i.pravatar.cc/80?img=${n}`} alt="" className="w-10 h-10 rounded-full border-2" style={{ borderColor: NAVY }} />
                ))}
              </div>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
                <span className="font-bold text-white">2,400+ members</span> building clean and sober love stories
              </p>
            </motion.div>
          </div>

          <div className="md:col-span-6 relative hidden md:block">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }} className="relative">
              <img
                src="/images/hero-couple.jpg"
                alt="Joyful couple"
                className="rounded-[2.5rem] w-full h-auto shadow-2xl"
                data-testid="hero-image"
              />
              <div className="absolute -left-8 top-16 bg-white rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3" style={{ border: "1px solid rgba(10,42,90,0.08)" }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(249,171,13,0.15)" }}>
                  <CalendarCheck size={20} style={{ color: GOLD }} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ ...mono, color: GOLD }}>Clean Time</p>
                  <p className="font-extrabold text-sm" style={{ color: NAVY }}>2 years, 4 months</p>
                </div>
              </div>
              <div className="absolute -right-4 bottom-16 bg-white rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3" style={{ border: "1px solid rgba(10,42,90,0.08)" }}>
                <Heart size={20} fill={GOLD} style={{ color: GOLD }} />
                <p className="font-bold text-sm" style={{ color: NAVY }}>It's a Match — 94% compatible</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENTO features */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ ...mono, color: GOLD }}>
          Why RecoveryMatch
        </motion.p>
        <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-14 max-w-2xl" style={{ ...serif, color: NAVY }}>
          Built for the way recovery actually works.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div {...fadeUp} className="md:col-span-7 rounded-3xl p-8 sm:p-10 relative overflow-hidden transition-transform hover:-translate-y-1" style={{ backgroundColor: NAVY }} data-testid="feature-matching">
            <Sparkles size={28} style={{ color: GOLD }} className="mb-6" />
            <h3 className="text-2xl text-white mb-3" style={serif}>Curated daily matches, not endless swiping</h3>
            <p className="max-w-md leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
              Our compatibility engine weighs your program, clean time, values and interests — then hand-delivers a
              few great matches a day with compatibility scores. Quality over quantity, always.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              {["AA", "NA", "SMART", "Celebrate"].map((p) => (
                <span key={p} className="rounded-full px-4 py-1.5 text-xs font-bold" style={{ backgroundColor: "rgba(249,171,13,0.15)", color: GOLD }}>{p}</span>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="md:col-span-5 bg-white rounded-3xl p-8 sm:p-10 transition-all hover:-translate-y-1 hover:shadow-md" style={{ border: "1px solid rgba(10,42,90,0.06)" }} data-testid="feature-safety">
            <ShieldCheck size={28} style={{ color: NAVY }} className="mb-6" />
            <h3 className="text-2xl mb-3" style={{ ...serif, color: NAVY }}>Safety isn't a feature. It's the foundation.</h3>
            <p className="leading-relaxed text-slate-600">
              Photo verification, date check-ins with trusted contacts, one-tap SOS, and a caring community —
              because your recovery comes first.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 bg-white rounded-3xl p-8 sm:p-10 transition-all hover:-translate-y-1 hover:shadow-md" style={{ border: "1px solid rgba(10,42,90,0.06)" }} data-testid="feature-guided">
            <MessageCircle size={28} style={{ color: GOLD }} className="mb-6" />
            <h3 className="text-xl mb-3 font-semibold" style={{ color: NAVY }}>Guided conversations</h3>
            <p className="text-sm leading-relaxed text-slate-600">Never stare at a blank message box. Thoughtful prompts help you go deeper than "hey."</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="md:col-span-4 bg-white rounded-3xl p-8 sm:p-10 transition-all hover:-translate-y-1 hover:shadow-md" style={{ border: "1px solid rgba(10,42,90,0.06)" }} data-testid="feature-ai">
            <Bot size={28} style={{ color: GOLD }} className="mb-6" />
            <h3 className="text-xl mb-3 font-semibold" style={{ color: NAVY }}>
              AI-powered profile & icebreakers
              <span className="ml-2 align-middle text-[10px] font-extrabold rounded-full px-2 py-0.5" style={{ backgroundColor: "rgba(249,171,13,0.18)", color: "#B87A00" }}>NEW</span>
            </h3>
            <p className="text-sm leading-relaxed text-slate-600">Let AI craft your perfect bio and suggest conversation starters, so your personality shines.</p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="md:col-span-4 rounded-3xl p-8 sm:p-10 transition-transform hover:-translate-y-1 relative overflow-hidden" style={{ backgroundColor: GOLD }} data-testid="feature-community">
            <Users size={28} style={{ color: NAVY }} className="mb-6" />
            <h3 className="text-xl mb-3 font-extrabold" style={{ color: NAVY }}>More than dating</h3>
            <p className="text-sm leading-relaxed font-medium" style={{ color: "rgba(10,42,90,0.8)" }}>
              A community feed, milestone celebrations, clean and sober events, and friendships that hold you accountable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* APP SHOWCASE */}
      <section id="app" className="py-20 sm:py-28" style={{ backgroundColor: "#F3EEE7" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ ...mono, color: GOLD }}>
                iOS & Android
              </motion.p>
              <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-xl" style={{ ...serif, color: NAVY }}>
                Take the journey with you.
              </motion.h2>
            </div>
            <motion.div {...fadeUp}>
              <StoreBadges />
            </motion.div>
          </div>
          <div className="flex gap-5 overflow-x-auto pb-6 -mx-5 px-5 sm:mx-0 sm:px-0" style={{ scrollbarWidth: "thin" }} data-testid="app-screens-strip">
            {appScreens.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="flex-shrink-0 w-52 sm:w-60"
              >
                <img src={s.image} alt={s.title} className="rounded-3xl w-full shadow-lg border" style={{ borderColor: "rgba(10,42,90,0.08)" }} loading="lazy" />
                <p className="mt-3 text-sm font-bold text-center" style={{ color: NAVY }}>{s.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section id="stories" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ ...mono, color: GOLD }}>
          Success Stories
        </motion.p>
        <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-14 max-w-2xl" style={{ ...serif, color: NAVY }}>
          Real couples. Real recovery. Real love.
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {successStories.map((story, i) => (
            <motion.div
              key={story.names}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              className="bg-white rounded-3xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg"
              style={{ border: "1px solid rgba(10,42,90,0.06)" }}
              data-testid={`story-card-${i}`}
            >
              <img src={story.image} alt={story.names} className="w-full h-56 object-cover" loading="lazy" />
              <div className="p-7">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-bold" style={{ color: NAVY }}>{story.names}</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1" style={{ ...mono, backgroundColor: "rgba(249,171,13,0.15)", color: "#B87A00" }}>
                    {story.duration}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-slate-600 italic">"{story.quote}"</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonial band */}
      <section style={{ backgroundColor: NAVY }} className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="flex justify-center gap-1 mb-8">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} fill={GOLD} style={{ color: GOLD }} />
            ))}
          </div>
          <p className="text-2xl sm:text-3xl lg:text-4xl text-white leading-snug italic mb-8" style={serif}>
            "For the first time, I didn't have to explain why I don't drink. She already knew. We celebrated my
            3-year anniversary together last month."
          </p>
          <p className="text-sm font-bold uppercase tracking-widest" style={{ ...mono, color: GOLD }}>
            A RecoveryMatch member — 3 years clean
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl tracking-tight mb-12 text-center" style={{ ...serif, color: NAVY }}>
          Questions, answered.
        </motion.h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(10,42,90,0.08)" }}>
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left font-bold text-sm sm:text-base"
                style={{ color: NAVY }}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-toggle-${i}`}
              >
                {faq.q}
                <ChevronDown size={18} className={`flex-shrink-0 ml-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} style={{ color: GOLD }} />
              </button>
              {openFaq === i && (
                <p className="px-6 pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-28 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-6xl tracking-tight mb-6" style={{ ...serif, color: NAVY }}>
          Your next chapter starts clean and sober.
        </h2>
        <p className="text-base sm:text-lg text-slate-600 mb-10 max-w-xl mx-auto">
          Join free today. Meet people who measure life the same way you do — one day at a time.
        </p>
        <button
          className="rounded-full px-10 py-5 font-bold text-lg transition-transform hover:scale-[1.03] active:scale-[0.98] shadow-lg"
          style={{ backgroundColor: GOLD, color: NAVY }}
          onClick={() => navigate("/register")}
          data-testid="final-cta"
        >
          Create Your Free Profile
        </button>
        <div className="mt-8 flex justify-center">
          <StoreBadges height="h-10" />
        </div>
        <p className="mt-6 text-xs uppercase tracking-widest font-bold" style={{ ...mono, color: "rgba(10,42,90,0.4)" }}>
          Free forever plan • No credit card • Cancel anytime
        </p>
      </section>

      <Footer />
    </div>
  );
}
